import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDI } from './DIContext';
import { CustomerProductAreaFamily, ProductFamily } from 'repositories/productFamily/ProductFamilyRepository';
import { useAccounts } from 'hooks/customer/Customer';
import { useAuth } from './AuthContext';
import { useProductRangeDraft } from 'hooks/productRange/ProductRangeDraft';
import { useProductRangeMatrix } from 'hooks/productRange/ProductRangeMatrixOperation';
import { ProductRangeApplyStatus } from './ProductRangeApplyStatus';
import { ProductRangeCustomerSelector } from './ProductRangeCustomerSelector';
import { ProductRangeTable } from './ProductRangeTable';
import { ProductRangeToolbar } from './ProductRangeToolbar';
import { CustomerOption } from './ProductRangeTypes';

const ProductRangeMatrix: React.FC = () => {
  const { draft, saveDraft, clearDraft } = useProductRangeDraft();
  const { productFamilyRepository } = useDI();
  const { token } = useAuth();
  const salesCompanyId = token?.activeRole.erpDataId;
  const { accounts, updateFilter } = useAccounts(salesCompanyId);
  const [productFamilies, setProductFamilies] = useState<ProductFamily[]>([]);
  const [productAreas, setProductAreas] = useState<string[]>([]);
  const [productFamilyError, setProductFamilyError] = useState<string | null>(null);
  const { matrix, setCustomerSelections, removeCustomer, clearMatrix, updateCustomerFamilies, updateSelectedCustomers } = useProductRangeMatrix(draft?.matrix ?? {});
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>(draft?.selectedCustomers ?? []);
  const [customerOptions, setCustomerOptions] = useState<Record<string, CustomerOption>>({});
  const [productAreaFilter, setProductAreaFilter] = useState(draft?.productAreaFilter ?? 'all');
  const [familyFilter, setFamilyFilter] = useState(draft?.familyFilter ?? '');
  const [expandedAreas, setExpandedAreas] = useState<Record<string, boolean>>(draft?.expandedAreas ?? {});
  const restoredCustomerIds = useRef(new Set((draft?.selectedCustomers ?? []).filter((customerId) => draft?.matrix?.[customerId] !== undefined)));
  const [applyStatus, setApplyStatus] = useState<'idle' | 'applying' | 'success' | 'error'>('idle');
  const [applyError, setApplyError] = useState<string | null>(null);
  const [customerSelectionError, setCustomerSelectionError] = useState<string | null>(null);
  const [templateMode, setTemplateMode] = useState(false);
  const [templateMatrix, setTemplateMatrix] = useState<Record<string, string[]>>({});

  const selectedCustomerObjects = useMemo(
    () => selectedCustomers.map((customerId) => customerOptions[customerId]).filter((customer): customer is CustomerOption => customer !== undefined),
    [customerOptions, selectedCustomers],
  );

  useEffect(() => {
    let isMounted = true;
    if (salesCompanyId === undefined || salesCompanyId === '') {
      return;
    }

    productFamilyRepository.findAll(salesCompanyId).then((response) => {
      if (!isMounted) return;
      setProductFamilies(response.families);
      setProductAreas(response.areas);
      setExpandedAreas((previous) => response.areas.reduce<Record<string, boolean>>((areas, area) => ({ ...areas, [area]: previous[area] ?? false }), {}));
    }).catch((error: Error) => {
      if (isMounted) setProductFamilyError(error.message);
    });

    return () => { isMounted = false; };
  }, [productFamilyRepository, salesCompanyId]);

  useEffect(() => {
    if (accounts === null) return;
    setCustomerOptions((previous) => ({
      ...previous,
      ...accounts.reduce<Record<string, CustomerOption>>((next, account) => {
        next[account.number] = {
          id: account.number,
          siteId: account.siteId,
          customerName: account.name,
          company: account.salesCompanyId,
          salesCompanyId: account.salesCompanyId,
        };
        return next;
      }, {}),
    }));
  }, [accounts]);

  const filteredCustomers = useMemo<CustomerOption[]>(() => (accounts ?? []).map((account) => ({
    id: account.number,
    siteId: account.siteId,
    customerName: account.name,
    company: account.salesCompanyId,
    salesCompanyId: account.salesCompanyId,
  })), [accounts]);

  const visibleFamilies = useMemo(() => productFamilies.filter((family) => (
    (productAreaFilter === 'all' || family.area === productAreaFilter) &&
    (familyFilter === '' || family.name.toLowerCase().includes(familyFilter.toLowerCase()))
  )), [familyFilter, productAreaFilter, productFamilies]);

  const groupedVisibleFamilies = useMemo(() => visibleFamilies.reduce<Array<{ area: string, families: ProductFamily[] }>>((groups, family) => {
    const group = groups.find((candidate) => candidate.area === family.area);
    if (group) {
      group.families.push(family);
    } else {
      groups.push({ area: family.area, families: [family] });
    }
    return groups;
  }, []), [visibleFamilies]);

  const availableCustomers = filteredCustomers.filter((customer) => !selectedCustomers.includes(customer.id));
  const hasMultipleSelectedCustomers = selectedCustomerObjects.length > 1;
  const allVisibleAreasExpanded = groupedVisibleFamilies.length > 0 && groupedVisibleFamilies.every((group) => expandedAreas[group.area] ?? false);
  const allVisibleFamiliesEnabledForSelectedCustomers = selectedCustomerObjects.length > 0 && visibleFamilies.length > 0 && selectedCustomerObjects.every((customer) => visibleFamilies.every((family) => matrix[customer.id]?.includes(family.id) ?? false));
  const someVisibleFamiliesEnabledForSelectedCustomers = selectedCustomerObjects.some((customer) => visibleFamilies.some((family) => matrix[customer.id]?.includes(family.id) ?? false));
  const allVisibleFamiliesEnabledForTemplate = visibleFamilies.length > 0 && visibleFamilies.every((family) => templateMatrix.template?.includes(family.id) ?? false);
  const someVisibleFamiliesEnabledForTemplate = visibleFamilies.some((family) => templateMatrix.template?.includes(family.id) ?? false);

  useEffect(() => {
    if (selectedCustomerObjects.length === 0 || productFamilies.length === 0) return;
    let isMounted = true;
    const customersToLoad = selectedCustomerObjects.filter((customer) => !restoredCustomerIds.current.has(customer.id));
    if (customersToLoad.length === 0) return;

    Promise.all(customersToLoad.map(async (customer) => {
      const selections = await productFamilyRepository.findCustomerSelections(customer.company, customer.id);
      return {
        customerId: customer.id,
        selectedFamilyIds: selections.length === 0 ? productFamilies.map((family) => family.id) : selections.map((selection) => `${selection.productAreaCode}-${selection.productFamilyCode}`),
      };
    })).then((customerSelections) => {
      if (!isMounted) return;
      customerSelections.forEach(({ customerId, selectedFamilyIds }) => {
        restoredCustomerIds.current.add(customerId);
        setCustomerSelections(customerId, selectedFamilyIds);
      });
    }).catch((error: Error) => {
      if (isMounted) setCustomerSelectionError(error.message);
    });

    return () => { isMounted = false; };
  }, [productFamilies, productFamilyRepository, selectedCustomerObjects, setCustomerSelections]);

  useEffect(() => {
    if (selectedCustomers.length === 0) {
      clearDraft();
      return;
    }
    saveDraft({ selectedCustomers, matrix, productAreaFilter, familyFilter, expandedAreas });
  }, [clearDraft, expandedAreas, familyFilter, matrix, productAreaFilter, saveDraft, selectedCustomers]);

  const toggleCustomerSelection = (customerId: string) => {
    if (selectedCustomers.includes(customerId)) {
      restoredCustomerIds.current.delete(customerId);
      setSelectedCustomers((previous) => previous.filter((id) => id !== customerId));
      removeCustomer(customerId);
    } else {
      setSelectedCustomers((previous) => [...previous, customerId]);
    }
  };

  const clearSelectedCustomers = () => {
    restoredCustomerIds.current.clear();
    setSelectedCustomers([]);
    clearMatrix();
  };

  const addAllCustomers = () => {
    setSelectedCustomers((previous) => [...previous, ...availableCustomers.map((customer) => customer.id)]);
  };

  const toggleAreaExpansion = (area: string) => setExpandedAreas((previous) => ({ ...previous, [area]: !(previous[area] ?? false) }));
  const toggleAllAreas = () => setExpandedAreas(productAreas.reduce<Record<string, boolean>>((next, area) => ({ ...next, [area]: !allVisibleAreasExpanded }), {}));
  const updateCustomerFamily = (customerId: string, familyId: string, enabled: boolean) => updateCustomerFamilies(customerId, [familyId], enabled);
  const updateAllFamiliesForCustomer = (customerId: string, enabled: boolean) => updateCustomerFamilies(customerId, visibleFamilies.map((family) => family.id), enabled);
  const updateAreaForCustomer = (customerId: string, area: string, enabled: boolean) => updateCustomerFamilies(customerId, visibleFamilies.filter((family) => family.area === area).map((family) => family.id), enabled);
  const updateAreaForSelectedCustomers = (area: string, enabled: boolean) => updateSelectedCustomers(selectedCustomers, visibleFamilies.filter((family) => family.area === area).map((family) => family.id), enabled);
  const applyBulkChange = (enabled: boolean) => updateSelectedCustomers(selectedCustomers, visibleFamilies.map((family) => family.id), enabled);
  const updateFamilyForSelectedCustomers = (familyId: string, enabled: boolean) => updateSelectedCustomers(selectedCustomers, [familyId], enabled);
  const updateAllFamiliesForTemplate = (enabled: boolean) => setTemplateMatrix({ template: enabled ? productFamilies.map((family) => family.id) : [] });
  const updateAreaForTemplate = (area: string, enabled: boolean) => {
    const familyIds = visibleFamilies.filter((family) => family.area === area).map((family) => family.id);
    setTemplateMatrix((previous) => {
      const next = new Set(previous.template ?? []);
      familyIds.forEach((familyId) => enabled ? next.add(familyId) : next.delete(familyId));
      return { template: Array.from(next) };
    });
  };
  const updateTemplateFamily = (familyId: string, enabled: boolean) => {
    setTemplateMatrix((previous) => {
      const next = new Set(previous.template ?? []);
      if (enabled) next.add(familyId); else next.delete(familyId);
      return { template: Array.from(next) };
    });
  };
  const toggleTemplateMode = () => {
    setTemplateMode((previous) => {
      if (!previous && templateMatrix.template === undefined) setTemplateMatrix({ template: productFamilies.map((family) => family.id) });
      return !previous;
    });
  };

  const applyTemplate = async () => {
    if (selectedCustomerObjects.length === 0) return;
    const selectedFamilyIds = templateMatrix.template ?? [];
    if (selectedFamilyIds.length === 0) {
      setApplyError('Select at least one product family in the template.');
      setApplyStatus('error');
      return;
    }
    setApplyStatus('applying');
    setApplyError(null);
    try {
      await Promise.all(selectedCustomerObjects.map((customer) => {
        const allFamiliesSelected = productFamilies.every((family) => selectedFamilyIds.includes(family.id));
        const selections = allFamiliesSelected ? [] : selectedFamilyIds.map((familyId): CustomerProductAreaFamily => {
          const separatorIndex = familyId.indexOf('-');
          return { productAreaCode: familyId.slice(0, separatorIndex), productFamilyCode: familyId.slice(separatorIndex + 1) };
        });
        return productFamilyRepository.putCustomerSelections(customer.company, customer.id, selections);
      }));
      selectedCustomerObjects.forEach((customer) => setCustomerSelections(customer.id, selectedFamilyIds));
      setApplyStatus('success');
    } catch (error) {
      setApplyError(error instanceof Error ? error.message : 'Unable to apply template.');
      setApplyStatus('error');
    }
  };

  const applyChanges = async () => {
    if (selectedCustomerObjects.length === 0) return;
    const customersWithoutSelections = selectedCustomerObjects.filter((customer) => !(matrix[customer.id] ?? []).some((familyId) => productFamilies.some((family) => family.id === familyId)));
    if (customersWithoutSelections.length > 0) {
      setApplyError(`Select at least one product family for each customer. Missing selection: ${customersWithoutSelections.map((customer) => `${customer.customerName} (${customer.id})`).join(', ')}`);
      setApplyStatus('error');
      return;
    }

    setApplyStatus('applying');
    setApplyError(null);
    try {
      await Promise.all(selectedCustomerObjects.map((customer) => {
        const selectedFamilyIds = matrix[customer.id] ?? [];
        const allFamiliesSelected = productFamilies.every((family) => selectedFamilyIds.includes(family.id));
        const selections = allFamiliesSelected ? [] : selectedFamilyIds.map((familyId): CustomerProductAreaFamily => {
          const separatorIndex = familyId.indexOf('-');
          return { productAreaCode: familyId.slice(0, separatorIndex), productFamilyCode: familyId.slice(separatorIndex + 1) };
        });
        return productFamilyRepository.putCustomerSelections(customer.company, customer.id, selections);
      }));
      clearDraft();
      setApplyStatus('success');
    } catch (error) {
      setApplyError(error instanceof Error ? error.message : 'Unable to apply changes.');
      setApplyStatus('error');
    }
  };

  return (
    <div className="p-6 md:p-8 flex flex-col gap-6">
      <ProductRangeApplyStatus status={applyStatus} error={applyError} />
      {customerSelectionError !== null && <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">Unable to load customer product family selections: {customerSelectionError}</div>}
      {productFamilyError !== null && <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">Unable to load product families: {productFamilyError}</div>}
      <div className="grid grid-cols-1 2xl:grid-cols-[340px_minmax(0,1fr)] gap-6">
        <ProductRangeCustomerSelector availableCustomers={availableCustomers} selectedCustomerObjects={selectedCustomerObjects} updateFilter={updateFilter} toggleCustomerSelection={toggleCustomerSelection} addAllCustomers={addAllCustomers} clearSelectedCustomers={clearSelectedCustomers} />
        <div className="bg-white rounded-lg shadow p-6 flex flex-col gap-4 min-w-0">
          <div><h2 className="text-lg font-semibold text-gray-900">Product area and family management in PWS</h2><p className="text-sm text-gray-500">Manage the product area and families for each customer.</p></div>
          {selectedCustomerObjects.length === 0 ? <div className="flex min-h-[420px] items-center justify-center rounded-lg border border-dashed border-gray-300 bg-slate-50 px-6 text-center"><p className="text-sm text-gray-500">Select at least one customer to view and edit product families.</p></div> : <>
            <ProductRangeToolbar productAreas={productAreas} productAreaFilter={productAreaFilter} allVisibleAreasExpanded={allVisibleAreasExpanded} setProductAreaFilter={setProductAreaFilter} setFamilyFilter={setFamilyFilter} toggleAllAreas={toggleAllAreas} templateMode={templateMode} toggleTemplateMode={toggleTemplateMode} />
            <ProductRangeTable selectedCustomerObjects={selectedCustomerObjects} visibleFamilies={visibleFamilies} groupedVisibleFamilies={groupedVisibleFamilies} expandedAreas={expandedAreas} matrix={matrix} hasMultipleSelectedCustomers={hasMultipleSelectedCustomers} allVisibleFamiliesEnabledForSelectedCustomers={allVisibleFamiliesEnabledForSelectedCustomers} someVisibleFamiliesEnabledForSelectedCustomers={someVisibleFamiliesEnabledForSelectedCustomers} toggleAreaExpansion={toggleAreaExpansion} applyBulkChange={applyBulkChange} updateAllFamiliesForCustomer={updateAllFamiliesForCustomer} updateAreaForCustomer={updateAreaForCustomer} updateAreaForSelectedCustomers={updateAreaForSelectedCustomers} updateFamilyForSelectedCustomers={updateFamilyForSelectedCustomers} updateCustomerFamily={updateCustomerFamily} templateMode={templateMode} templateMatrix={templateMatrix} allVisibleFamiliesEnabledForTemplate={allVisibleFamiliesEnabledForTemplate} someVisibleFamiliesEnabledForTemplate={someVisibleFamiliesEnabledForTemplate} updateAllFamiliesForTemplate={updateAllFamiliesForTemplate} updateAreaForTemplate={updateAreaForTemplate} updateTemplateFamily={updateTemplateFamily} />
          </>}
          {selectedCustomerObjects.length > 0 && <div className="flex justify-end border-t border-gray-200 pt-4"><button type="button" onClick={() => { void (templateMode ? applyTemplate() : applyChanges()); }} disabled={applyStatus === 'applying'} className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60">{applyStatus === 'applying' ? 'Applying...' : templateMode ? 'Apply template' : 'Apply changes'}</button></div>}
        </div>
      </div>
    </div>
  );
};

export default ProductRangeMatrix;
